/**
 * usePersistedState.ts — persisted user settings, in two layers.
 *
 * WHY NOT IP: a profile keyed on IP address would be shared between every
 * subscriber behind a carrier's CGNAT and lost on every tower handoff or
 * Wi-Fi/cellular switch. Storage is per-device instead.
 *
 * LAYER 1 — localStorage. Synchronous, so settings are already correct on the
 * very first render. This is the source of truth in normal operation.
 *
 * LAYER 2 — a server-set cookie via /api/settings, purely as an iOS backstop.
 * Safari's ITP deletes all SCRIPT-WRITABLE storage (localStorage, IndexedDB,
 * document.cookie) after 7 days without a first-party visit; cookies set by the
 * server through Set-Cookie are exempt. So the same blob is mirrored to that
 * endpoint, and when iOS wipes layer 1 the client silently rehydrates from
 * layer 2. See functions/api/settings.ts for the server half.
 *
 * DURABILITY, once both layers are deployed:
 *   • Desktop, Android Chrome, Android WebView — until the user clears site
 *     data. Layer 1 alone already covered this.
 *   • iOS Safari / WKWebView — ~400 days, rolling (every save re-issues the
 *     cookie), instead of 7 days of inactivity.
 *   • Home-screen installs — exempt from the cap either way.
 *   • Private browsing — cleared at session end. Nothing fixes that, nor
 *     should it.
 *
 * The network layer is strictly best-effort. Every remote path fails silently
 * to layer 1: a blocked fetch, an offline device, or an un-deployed endpoint
 * degrades this file to exactly its previous behaviour.
 *
 * SCHEMA VERSION: bump SCHEMA_VERSION when a persisted value changes shape.
 * It's part of the localStorage key, so stale entries are ignored rather than
 * parsed into something the app can't handle.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type { Dispatch, SetStateAction } from "react";

const STORAGE_PREFIX = "sc.map";
const SCHEMA_VERSION = 1;
const WRITE_DEBOUNCE_MS = 250;

/** Server mirror. Same origin — a cross-origin endpoint would forfeit the ITP exemption. */
const REMOTE_PATH = "/api/settings";
/** Longer than the local debounce: this one costs a request, not a memcpy. */
const REMOTE_DEBOUNCE_MS = 1500;
/** Grace period before seeding the cookie for someone whose settings never change. */
const REMOTE_SEED_DELAY_MS = 4000;

const keyPrefix = `${STORAGE_PREFIX}.v${SCHEMA_VERSION}.`;
const storageKey = (key: string) => `${keyPrefix}${key}`;

// ─── Layer 1: localStorage ────────────────────────────────────────────────────

/**
 * Probe once, at module load, rather than try/catching on every write. Safari in
 * private mode has historically thrown QuotaExceededError on setItem, and
 * embedded WebViews can have storage disabled outright.
 */
const storageAvailable: boolean = (() => {
  try {
    if (typeof window === "undefined" || !window.localStorage) return false;
    const probe = `${STORAGE_PREFIX}.probe`;
    window.localStorage.setItem(probe, "1");
    window.localStorage.removeItem(probe);
    return true;
  } catch {
    return false;
  }
})();

/**
 * Ask the browser to exempt this origin from eviction when the device runs low
 * on disk. Chrome/Firefox honour it; Safari resolves false. Fire-and-forget —
 * and note it does nothing for the iOS 7-day cap, which is what layer 2 is for.
 */
if (storageAvailable && typeof navigator !== "undefined") {
  navigator.storage?.persist?.().catch(() => {});
}

/**
 * Writes are debounced: the opacity and label-size sliders fire on every pointer
 * move, and localStorage.setItem is a synchronous main-thread write that shows
 * up as scroll jank on low-end Android.
 *
 * The cost is a window where a pending value dies if the page is killed — and
 * mobile OSes kill backgrounded tabs aggressively without firing `unload`. So
 * pending writes flush on `pagehide` and visibilitychange→hidden, the two events
 * iOS actually guarantees.
 */
const pending = new Map<string, string>();
let flushTimer: ReturnType<typeof setTimeout> | null = null;

function flushPending() {
  if (flushTimer !== null) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (!storageAvailable || pending.size === 0) return;
  for (const [key, value] of pending) {
    try {
      window.localStorage.setItem(key, value);
    } catch {
      // Quota exceeded or storage revoked mid-session. Settings are a nicety;
      // drop the write rather than surface an error to the user.
    }
  }
  pending.clear();
}

function queueWrite(key: string, value: string) {
  if (!storageAvailable) return;
  pending.set(key, value);
  if (flushTimer === null) flushTimer = setTimeout(flushPending, WRITE_DEBOUNCE_MS);
}

function localNamespaceEmpty(): boolean {
  if (!storageAvailable) return true;
  try {
    for (let i = 0; i < window.localStorage.length; i++) {
      const k = window.localStorage.key(i);
      if (k && k.startsWith(keyPrefix)) return false;
    }
  } catch {
    return true;
  }
  return true;
}

// ─── Layer 2: server-set cookie ───────────────────────────────────────────────

/** Live snapshot of every persisted setting, posted to the server as one blob. */
const mirror: Record<string, unknown> = {};
let remoteTimer: ReturnType<typeof setTimeout> | null = null;
let remoteEverPosted = false;
/** Set after a 4xx, so a missing or rejecting endpoint costs one request, not one per change. */
let remoteDisabled = false;

function postMirror(useBeacon = false) {
  if (remoteTimer !== null) {
    clearTimeout(remoteTimer);
    remoteTimer = null;
  }
  if (remoteDisabled || typeof window === "undefined") return;
  if (Object.keys(mirror).length === 0) return;

  let body: string;
  try {
    body = JSON.stringify(mirror);
  } catch {
    return;
  }
  remoteEverPosted = true;

  // On pagehide a normal fetch is routinely killed mid-flight; sendBeacon is
  // handed to the browser to deliver after the page is gone. The server reads
  // the body as text precisely because beacons carry their own content type.
  if (useBeacon && navigator.sendBeacon) {
    try {
      navigator.sendBeacon(REMOTE_PATH, new Blob([body], { type: "text/plain" }));
      return;
    } catch {
      // Fall through to fetch.
    }
  }

  fetch(REMOTE_PATH, {
    method: "POST",
    // Explicit, though same-origin is the default: without cookies the server
    // can't set the cookie that is the entire point of this layer.
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body,
    keepalive: true,
  })
    .then((res) => {
      // 4xx means this build and the endpoint disagree about the payload, or
      // the function isn't deployed. Retrying every change would be noise.
      if (res.status >= 400 && res.status < 500) remoteDisabled = true;
    })
    .catch(() => {
      // Offline or blocked. Layer 1 already has the value; try again next change.
    });
}

function queueRemote() {
  if (remoteDisabled) return;
  if (remoteTimer !== null) clearTimeout(remoteTimer);
  remoteTimer = setTimeout(() => postMirror(false), REMOTE_DEBOUNCE_MS);
}

/**
 * Read the mirror back. Started at module load — before React renders — so the
 * round trip overlaps with map init instead of following it.
 *
 * Only runs when there's nothing in localStorage: either a genuine first visit,
 * or an iOS user whose storage was just wiped. Those are indistinguishable from
 * here, and the handling is the same for both.
 */
const remoteHydration: Promise<Record<string, unknown>> | null =
  typeof fetch !== "undefined" && localNamespaceEmpty()
    ? fetch(REMOTE_PATH, { credentials: "same-origin", cache: "no-store" })
        .then((res) => (res.ok ? res.json() : {}))
        .then((data) =>
          data && typeof data === "object" && !Array.isArray(data)
            ? (data as Record<string, unknown>)
            : {},
        )
        .catch(() => ({}))
    : null;

if (typeof window !== "undefined") {
  const onHide = () => {
    flushPending();
    postMirror(true);
  };
  window.addEventListener("pagehide", onHide);
  window.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") onHide();
  });

  // Seed the cookie for users who already have local settings and never touch a
  // control this session — without this they'd stay on the 7-day clock forever,
  // since the mirror otherwise only posts on change.
  setTimeout(() => {
    if (!remoteEverPosted) postMirror(false);
  }, REMOTE_SEED_DELAY_MS);
}

// ─── The hook ─────────────────────────────────────────────────────────────────

/**
 * Drop-in replacement for useState that persists to localStorage, mirrors to the
 * server cookie, and rehydrates from it when local storage comes up empty.
 *
 * @param key      Stable name for the setting. Namespaced and versioned inside.
 * @param initial  Default value, or a lazy factory for an expensive/dynamic one.
 * @param validate Optional guard. Stored values are untrusted input — they may
 *                 come from an older build, a hand-edited devtools session, or
 *                 the network. Anything failing the guard falls back to
 *                 `initial` instead of poisoning app state.
 */
export function usePersistedState<T>(
  key: string,
  initial: T | (() => T),
  validate?: (value: unknown) => boolean,
): [T, Dispatch<SetStateAction<T>>] {
  const fullKey = storageKey(key);
  // Held in a ref so the effects below never re-run on identity change.
  const validateRef = useRef(validate);
  validateRef.current = validate;

  const hadLocalValue = useRef(false);
  const [value, setValue] = useState<T>(() => {
    const fallback = () =>
      typeof initial === "function" ? (initial as () => T)() : initial;
    if (!storageAvailable) return fallback();
    try {
      const raw = window.localStorage.getItem(fullKey);
      if (raw === null) return fallback();
      const parsed = JSON.parse(raw) as unknown;
      if (validateRef.current && !validateRef.current(parsed)) return fallback();
      hadLocalValue.current = true;
      return parsed as T;
    } catch {
      return fallback();
    }
  });

  /**
   * Set once the app itself changes the value. Guards the rehydration below: if
   * the user has already flipped a toggle, a late-arriving server value must not
   * yank it back.
   */
  const touched = useRef(false);
  const setPersisted = useCallback<Dispatch<SetStateAction<T>>>((next) => {
    touched.current = true;
    setValue(next);
  }, []);

  // Rehydrate from the server mirror when this key had nothing locally.
  useEffect(() => {
    if (!remoteHydration || hadLocalValue.current) return;
    let cancelled = false;
    remoteHydration.then((data) => {
      if (cancelled || touched.current) return;
      const incoming = data[key];
      if (incoming === undefined) return;
      if (validateRef.current && !validateRef.current(incoming)) return;
      setValue(incoming as T);
    });
    return () => {
      cancelled = true;
    };
  }, [key]);

  // Persist. First run only seeds the mirror: writing would rewrite the value we
  // just read, and would clobber a good stored value with the default whenever
  // storage was unavailable at read time.
  const hydrated = useRef(false);
  useEffect(() => {
    mirror[key] = value;
    if (!hydrated.current) {
      hydrated.current = true;
      return;
    }
    try {
      queueWrite(fullKey, JSON.stringify(value));
    } catch {
      // Non-serialisable value (cyclic, undefined). Nothing to persist.
    }
    queueRemote();
  }, [fullKey, key, value]);

  return [value, setPersisted];
}

/** Wipe every persisted setting — local, and the server mirror. */
export function clearPersistedSettings(): void {
  if (storageAvailable) {
    try {
      const doomed: string[] = [];
      for (let i = 0; i < window.localStorage.length; i++) {
        const k = window.localStorage.key(i);
        if (k && k.startsWith(keyPrefix)) doomed.push(k);
      }
      doomed.forEach((k) => window.localStorage.removeItem(k));
    } catch {
      // Storage revoked. Nothing to clear.
    }
  }
  pending.clear();
  for (const k of Object.keys(mirror)) delete mirror[k];
  if (remoteTimer !== null) {
    clearTimeout(remoteTimer);
    remoteTimer = null;
  }
  if (typeof fetch !== "undefined") {
    fetch(REMOTE_PATH, { method: "DELETE", credentials: "same-origin" }).catch(() => {});
  }
}

// ─── Validators for the settings app.tsx persists ─────────────────────────────
export const isBool = (v: unknown): boolean => typeof v === "boolean";
export const isString = (v: unknown): boolean => typeof v === "string";
export const isStringOrNull = (v: unknown): boolean =>
  v === null || typeof v === "string";
/** Numeric guard that also rejects NaN and out-of-range values from old builds. */
export const isNumberInRange =
  (min: number, max: number) =>
  (v: unknown): boolean =>
    typeof v === "number" && Number.isFinite(v) && v >= min && v <= max;
/** Object whose listed keys are all booleans. Extra keys are ignored. */
export const isBoolRecord =
  (keys: string[]) =>
  (v: unknown): boolean =>
    typeof v === "object" &&
    v !== null &&
    keys.every((k) => typeof (v as Record<string, unknown>)[k] === "boolean");
